import { ChangeEvent, Dispatch, SetStateAction, useState } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button } from "@mui/material";
import * as Yup from "yup";
import { isNumber, isValidInput } from "../utils/validations";
import useTranslation from "../hooks/traslation";
// Validation Schema

// const subCategorySchema = Yup.object().shape({
//   name: Yup.string()
//     .min(2, "Sub Service name must be at least 2 characters")
//     .max(50, "Sub Service name must not exceed 50 characters")
//     .nullable(),
//   name_ar: Yup.string()
//     .min(2, "Sub Service name arabic must be at least 2 characters")
//     .max(50, "Sub Service name arabic must not exceed 50 characters")
//     .nullable(),
//   price: Yup.string().required("Price is required").min(1, "Price is required"),
//   description: Yup.string()
//     .max(500, "Description must not exceed 500 characters")
//     .nullable(),
// });

interface AddCategoryModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  name: string;
  price: string;
  description: string;
  setName: Dispatch<SetStateAction<string>>;
  setPrice: Dispatch<SetStateAction<string>>;
  setDescription: Dispatch<SetStateAction<string>>;
  handleSubmit: () => Promise<void>;
  selectedId: string;
  setSelectedId: Dispatch<SetStateAction<string>>;

  name_ar: string;
  description_ar: string;
  setName_ar: Dispatch<SetStateAction<string>>;
  setDescription_ar: Dispatch<SetStateAction<string>>;
}

export default function AddSubCategory({
  open,
  onClose,
  setOpen,
  name,
  price,
  description,
  setName,
  setPrice,
  setDescription,
  handleSubmit,
  selectedId,
  setSelectedId,
  description_ar,
  name_ar,
  setDescription_ar,
  setName_ar,
}: AddCategoryModalProps) {
  const [errors, setErrors] = useState({
    name: "",
    name_ar: "",
    price: "",
    description: "",
    description_ar: "",
  });
  const translate = useTranslation()

  const subCategorySchema = Yup.object().shape({
    name: Yup.string()
      .required(translate?.error?.required)
      .min(2, translate?.auth?.subServiceCharLen_min)
      .max(50, translate?.auth?.subServiceCharLen_max),
    name_ar: Yup.string()
      .required(translate?.error?.required)
      .min(2, translate?.auth?.subServiceCharLen_min)
      .max(50, translate?.auth?.subServiceCharLen_max),
    price: Yup.string()
      .required(translate?.auth?.priceRequired)
      .min(1, translate?.auth?.priceRequired),
    description: Yup.string()
      .max(500, translate?.auth?.descriptionLimit),
    descriptionAr: Yup.string()
      .max(500, translate?.auth?.arabicDescriptionLimit),
  });
  

  const validateForm = async () => {
    try {
      await subCategorySchema.validate(
        { name, price, description, name_ar, description_ar },
        { abortEarly: false }
      );
      setErrors({
        name: "",
        price: "",
        description: "",
        description_ar: "",
        name_ar: "",
      });
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = {
          name: "",
          price: "",
          description: "",
          name_ar: "",
          description_ar: "",
        };
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof typeof newErrors] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleFormSubmit = async () => {
    if (await validateForm()) {
      handleSubmit();
    }
  };

  const handleClose = () => {
    setSelectedId("");
    setName("");
    setPrice("");
    setDescription("");
    setOpen(false);
    setErrors({
      name: "",
      price: "",
      description: "",
      description_ar: "",
      name_ar: "",
    });
  };

  return (
    <Modal
      className="modal addCategory_modal"
      open={open}
      onClose={handleClose}
    >
      <div className="modal-dialog w_900">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{selectedId ? "Update Sub Service" : "Add Sub Service"}</h2>
            <div className="btn-close" onClick={handleClose}>
              <CloseIcon />
            </div>
          </div>

          <form className="form" action=""> 
            <div className="gap_p">
              <div className="control_group w_50">
                <label>{translate?.user?.subServiceNameEnglish} <span style={{ color: 'red' }}>*</span></label>
                <TextField
                  placeholder={translate?.user?.enterSubServiceName}
                  fullWidth
                  value={name}
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      setName(e.target.value);
                      setErrors((prev) => ({ ...prev, name: "" }));
                    }
                  }}
                  error={!!errors.name}
                  hiddenLabel
                />
                {errors.name && (
                  <span
                    className="error-message"
                    style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                  >
                    {errors.name}
                  </span>
                )}
              </div>

              <div className="control_group w_50">
                <label>{translate?.user?.subServiceNameArabic} <span style={{ color: 'red' }}>*</span></label>
                <TextField
                  placeholder={translate?.user?.enterSubServiceName}
                  fullWidth
                  value={name_ar}
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      setName_ar(e.target.value);
                      setErrors((prev) => ({ ...prev, name_ar: "" }));
                    }
                  }}
                  error={!!errors.name_ar}
                  hiddenLabel
                />
                {errors.name_ar && (
                  <span
                    className="error-message"
                    style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                  >
                    {errors.name_ar}
                  </span>
                )}
              </div>

              <div className="control_group w_50">
                <label>{translate?.globals?.description} {translate?.main?.english}</label>
                <TextField
                  variant="outlined"
                  placeholder={translate?.globals?.description}
                  fullWidth
                  hiddenLabel
                  multiline
                  maxRows={5}
                  minRows={5}
                  inputProps={{ maxLength: 500 }}
                  value={description}
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      setDescription(e.target.value);
                      setErrors((prev) => ({ ...prev, description: "" }));
                    }
                  }}
                  error={!!errors.description}
                />
                {errors.description && (
                  <span
                    className="error-message"
                    style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                  >
                    {errors.description}
                  </span>
                )}
              </div>

              <div className="control_group w_50">
                <label>{translate?.globals?.description} {translate?.main?.arabic}</label>
                <TextField
                  variant="outlined"
                  placeholder={translate?.globals?.description}
                  fullWidth
                  hiddenLabel
                  multiline
                  maxRows={5}
                  minRows={5}
                  inputProps={{ maxLength: 500 }}
                  value={description_ar}
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      setDescription_ar(e.target.value);
                      setErrors((prev) => ({ ...prev, description_ar: "" }));
                    }
                  }}
                  error={!!errors.description_ar}
                />
                {errors.description_ar && (
                  <span
                    className="error-message"
                    style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                  >
                    {errors.description_ar}
                  </span>
                )}
              </div>

              <div className="control_group w_50">
                <label>{translate?.main?.basePrice} <span style={{ color: 'red' }}>*</span></label>
                <TextField
                  placeholder={translate?.user?.enterBasePrice}
                  fullWidth
                  type="text"
                  value={price}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string for backspace functionality
                    if (value === '') {
                      setPrice('');
                      setErrors((prev) => ({ ...prev, price: "" }));
                      return;
                    }
                    
                    // Check if the input matches the pattern: up to 10 digits with up to 2 decimal places
                    const priceRegex = /^\d{1,10}(\.\d{0,2})?$/;
                    
                    if (priceRegex.test(value)) {
                      // Convert to number to check if it's greater than 0
                      const numValue = parseFloat(value);
                      if (numValue > 0) {
                        setPrice(value);
                        setErrors((prev) => ({ ...prev, price: "" }));
                      }
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedText = e.clipboardData.getData('text');
                    const priceRegex = /^\d{1,10}(\.\d{0,2})?$/;
                    
                    if (priceRegex.test(pastedText)) {
                      const numValue = parseFloat(pastedText);
                      if (numValue > 0) {
                        setPrice(pastedText);
                        setErrors((prev) => ({ ...prev, price: "" }));
                      }
                    }
                  }}
                  inputProps={{ pattern: "[0-9]*\\.?[0-9]*" }}
                  error={!!errors.price}
                  hiddenLabel
                />
                {errors.price && (
                  <span
                    className="error-message"
                    style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                  >
                    {errors.price}
                  </span>
                )}
              </div>

              <div className="footer_btn_flex" >
                <Button className="btn btn_grey" onClick={handleClose}>
                  {translate?.main?.cancel}
                </Button>
                <Button className="btn btn_primary" onClick={handleFormSubmit}>
                  {selectedId ? translate?.main?.update : translate?.globals?.submit}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
